import { ApiError } from "@/lib/apiClient";
import {
  createGdInvoiceDraft,
  createHeroInvoiceDraft,
  linkGdRechnung,
  linkHeroRechnung,
  reassignHeroInvoice,
  type HeroProjectInvoiceOption,
} from "@/features/timesheetFlow/timesheetFlowApi";
import type {
  InvoiceDraftMode,
  InvoiceDraftProvider,
} from "@/features/timesheetFlow/timesheetBillingUtils";
import type { TimesheetRecord } from "@/features/timesheetFlow/timesheetFlowTypes";

export async function runTimesheetGdInvoiceLink(
  timesheetId: number,
  gdRechnungId: number,
  options: {
    onReload: () => Promise<void>;
    onRefresh: () => void;
    onError: (message: string) => void;
    onOpenGdRechnung?: (docId: number) => void;
  },
): Promise<void> {
  const { onReload, onRefresh, onError, onOpenGdRechnung } = options;
  if (!Number.isFinite(gdRechnungId) || gdRechnungId <= 0) {
    onError("Bitte eine Rechnung auswählen.");
    return;
  }
  try {
    const result = await linkGdRechnung(timesheetId, gdRechnungId);
    const docId = Number(result.gd_rechnung_id ?? gdRechnungId);
    await onReload();
    onRefresh();
    if (Number.isFinite(docId) && docId > 0) {
      onOpenGdRechnung?.(docId);
    }
  } catch (err: unknown) {
    const message =
      err instanceof ApiError
        ? err.message
        : err instanceof Error
          ? err.message
          : "Rechnung konnte nicht verknüpft werden.";
    onError(message);
  }
}

export async function runTimesheetHeroInvoiceLink(
  timesheetId: number,
  invoice: HeroProjectInvoiceOption,
  options: {
    onReload: () => Promise<void>;
    onRefresh: () => void;
    onError: (message: string) => void;
  },
): Promise<void> {
  const { onReload, onRefresh, onError } = options;
  const customerId = invoice.customer_document_id ?? invoice.id ?? invoice.document_id;
  const heroDocId = invoice.hero_document_id ?? invoice.document_id ?? invoice.id ?? customerId;
  if (customerId == null || String(customerId).trim() === "") {
    onError("Bitte eine Hero-Rechnung auswählen.");
    return;
  }
  try {
    await linkHeroRechnung(timesheetId, {
      invoice_document_id: String(heroDocId),
      invoice_customer_document_id: String(customerId),
    });
    await onReload();
    onRefresh();
  } catch (err: unknown) {
    const message =
      err instanceof ApiError
        ? err.message
        : err instanceof Error
          ? err.message
          : "Hero-Rechnung konnte nicht verknüpft werden.";
    onError(message);
  }
}

export async function runTimesheetInvoiceDraftAction(
  timesheetId: number,
  timesheet: TimesheetRecord,
  status: string,
  mode: InvoiceDraftMode,
  options: {
    provider?: InvoiceDraftProvider;
    selectedInvoiceId?: string;
    invoices?: HeroProjectInvoiceOption[];
    timesheetIds?: number[];
    onReload: () => Promise<void>;
    onRefresh: () => void;
    onError: (message: string) => void;
    onOpenGdRechnung?: (docId: number) => void;
  },
): Promise<void> {
  const {
    provider = "gd",
    selectedInvoiceId,
    invoices = [],
    timesheetIds,
    onReload,
    onRefresh,
    onError,
    onOpenGdRechnung,
  } = options;

  if (mode === "open") {
    const gdRechnungId = Number(timesheet.gd_rechnung_id ?? 0);
    if (provider === "gd" || (Number.isFinite(gdRechnungId) && gdRechnungId > 0)) {
      if (!Number.isFinite(gdRechnungId) || gdRechnungId <= 0) {
        onError("Kein Rechnungsentwurf in der Ernsthaus-App vorhanden.");
        return;
      }
      onOpenGdRechnung?.(gdRechnungId);
      return;
    }

    const docId = timesheet.invoice_customer_document_id;
    if (!docId) {
      onError("Kein Rechnungsentwurf vorhanden.");
      return;
    }
    window.open(`https://login.hero-software.de/partner/Documents/edit/${docId}`, "_blank", "noopener");
    return;
  }

  if (mode === "reassign") {
    if (!selectedInvoiceId) {
      onError("Bitte eine Rechnung auswählen.");
      return;
    }
    const inv = invoices.find(
      (i) => String(i.customer_document_id ?? i.id ?? i.document_id) === selectedInvoiceId,
    );
    const heroDocId = inv?.hero_document_id ?? inv?.document_id ?? inv?.id;
    await reassignHeroInvoice(timesheetId, {
      invoice_document_id: String(heroDocId || selectedInvoiceId),
      invoice_customer_document_id: selectedInvoiceId,
    });
    await onReload();
    onRefresh();
    return;
  }

  if (status !== "signed") {
    onError("Rechnungsentwurf nur für unterschriebene Nachweise möglich.");
    return;
  }

  if (provider === "gd") {
    const json = await createGdInvoiceDraft(
      timesheetId,
      timesheetIds?.length ? { timesheet_ids: timesheetIds } : undefined,
    );
    const docId = Number(json.gd_rechnung_id ?? 0);
    if (!Number.isFinite(docId) || docId <= 0) {
      onError("Rechnungsentwurf konnte in der Ernsthaus-App nicht erstellt werden.");
      return;
    }
    await onReload();
    onRefresh();
    onOpenGdRechnung?.(docId);
    return;
  }

  const json = await createHeroInvoiceDraft(timesheetId);
  if (json.hero_document_url) {
    window.open(json.hero_document_url, "_blank", "noopener");
  }
  await onReload();
  onRefresh();
}
